import * as React from "react";
import type { TypePeekInPanelProps } from "./PeekInTypes";

/**
 * Single source of truth for the panel's open/closed state.
 *
 * The panel slot declares its own controlled/uncontrolled props (`open`,
 * `defaultOpen`, `onOpenChange`), but the state itself lives on the parent
 * PeekIn — the action-bar toggle and the panel both need to reach it. Takes
 * the already-extracted panel element so both PeekInTailwind and PeekInStyled
 * can share one implementation.
 *
 * Returns a Dispatch-shaped setter so existing updater-form callers
 * (`setPanelOpen((prev) => !prev)`) keep working and PeekInContextValue's
 * signature is unchanged.
 */
export function usePeekInPanelState(panel: React.ReactElement | null) {
  const panelProps = panel?.props as TypePeekInPanelProps | undefined;
  const isControlled = panelProps?.open !== undefined;
  const onOpenChange = panelProps?.onOpenChange;

  const [internalOpen, setInternalOpen] = React.useState(
    panelProps?.defaultOpen ?? true
  );
  const panelOpen = isControlled ? panelProps.open! : internalOpen;

  const setPanelOpen = React.useCallback<
    React.Dispatch<React.SetStateAction<boolean>>
  >(
    (next) => {
      const value = typeof next === "function" ? next(panelOpen) : next;
      if (!isControlled) setInternalOpen(value);
      onOpenChange?.(value);
    },
    [isControlled, onOpenChange, panelOpen]
  );

  return { panelOpen, setPanelOpen };
}
